import { API_BASE_URL } from './client.js';
import { getAccessToken } from '../auth/supabase.js';

/**
 * Saving a file from an API that authenticates with a header.
 *
 * The obvious way is an anchor pointing at the endpoint, and it is what the
 * export page did. It cannot work: a browser following a link sends no
 * Authorization header, this API is Bearer only, and the server answers 401.
 * The file endpoints were correct the whole time and the one control that
 * reached them was not, which is why nobody caught it: the export built
 * successfully and only the last click failed.
 *
 * So the file is fetched properly, with the token, and handed to the browser
 * as a blob. The save dialog is identical; the request is one the server will
 * actually answer.
 */
export async function downloadFile(path: string, filename: string): Promise<void> {
  const token = await getAccessToken();

  const response = await fetch(`${API_BASE_URL}${path}`, {
    headers: token === null ? {} : { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    // The server explains itself in the envelope. Preferred over a generic
    // failure, because "this period has nothing in it" is a different problem
    // from "you are not allowed to do that".
    let message = `That file could not be downloaded (${String(response.status)}).`;
    try {
      const body = (await response.json()) as { error?: { message?: string } };
      if (typeof body.error?.message === 'string') message = body.error.message;
    } catch {
      // A non-JSON failure keeps the generic sentence above.
    }
    throw new Error(message);
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);

  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    // Revoked on the next tick: Safari has not finished reading the blob when
    // click() returns, and revoking immediately saves an empty file.
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }
}
